import React, { useEffect, useState } from 'react';
import AppToast from './AppToast';
import { managementAPI } from '../services/api';
import AddressAutocompleteMap from './AddressAutocompleteMap';

const OwnerConfiguration = ({ selectedCompanyId, onCompanyCreated }) => {
  const [sites, setSites] = useState([]);
  const [siteForm, setSiteForm] = useState({
    name: '',
    address: '',
    lat: '',
    lng: '',
    radius: '100',
  });
  const [showSiteModal, setShowSiteModal] = useState(false);
  const [addressMapKey, setAddressMapKey] = useState(0);
  const [editingSiteId, setEditingSiteId] = useState(null);
  const [shiftsBySite, setShiftsBySite] = useState({});
  const [showShiftModal, setShowShiftModal] = useState(false);
  const [shiftEditOriginalIds, setShiftEditOriginalIds] = useState(null);
  const [expandedShiftGroups, setExpandedShiftGroups] = useState({});
  const [shiftForm, setShiftForm] = useState({
    factory_id: '',
    name: '',
    grace_minutes: '10',
    slots: [
      { weekday: '1', start_time: '13:00', end_time: '19:00' },
      { weekday: '6', start_time: '13:00', end_time: '18:00' },
    ],
  });
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [configSection, setConfigSection] = useState('sedes');

  const openNewSiteModal = () => {
    setErr('');
    setMsg('');
    setSiteForm({
      name: '',
      address: '',
      lat: '',
      lng: '',
      radius: '100',
    });
    setAddressMapKey((k) => k + 1);
    setEditingSiteId(null);
    setShowSiteModal(true);
  };

  const openEditSiteModal = (site) => {
    setErr('');
    setMsg('');
    setSiteForm({
      name: site?.name || '',
      address: site?.address || '',
      lat: site?.latitude != null ? String(site.latitude) : '',
      lng: site?.longitude != null ? String(site.longitude) : '',
      radius: site?.radius_meters != null ? String(site.radius_meters) : '100',
    });
    setEditingSiteId(site?.id || null);
    setAddressMapKey((k) => k + 1);
    setShowSiteModal(true);
  };

  const weekdayOptions = [
    { value: 0, label: 'Domingo' },
    { value: 1, label: 'Lunes' },
    { value: 2, label: 'Martes' },
    { value: 3, label: 'Miércoles' },
    { value: 4, label: 'Jueves' },
    { value: 5, label: 'Viernes' },
    { value: 6, label: 'Sábado' },
  ];
  const weekdayShort = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

  const formatDayGroup = (days) => {
    if (!days.length) return '';
    const sorted = [...days].sort((a, b) => a - b);
    const ranges = [];
    let start = sorted[0];
    let prev = sorted[0];
    for (let i = 1; i < sorted.length; i += 1) {
      const curr = sorted[i];
      if (curr === prev + 1) {
        prev = curr;
      } else {
        ranges.push([start, prev]);
        start = curr;
        prev = curr;
      }
    }
    ranges.push([start, prev]);
    return ranges
      .map(([a, b]) => (a === b ? weekdayShort[a] : `${weekdayShort[a]} a ${weekdayShort[b]}`))
      .join(', ');
  };

  const openNewShiftModal = () => {
    setErr('');
    setMsg('');
    setShiftEditOriginalIds(null);
    setShiftForm({
      factory_id: '',
      name: '',
      grace_minutes: '10',
      slots: [
        { weekday: '1', start_time: '13:00', end_time: '19:00' },
        { weekday: '6', start_time: '13:00', end_time: '18:00' },
      ],
    });
    setShowShiftModal(true);
  };

  const closeShiftModal = () => {
    setShowShiftModal(false);
    setShiftEditOriginalIds(null);
  };

  const openEditShiftModal = (site, shiftName, detailRows) => {
    const sorted = [...detailRows].sort(
      (a, b) => a.weekday - b.weekday || String(a.start_time).localeCompare(String(b.start_time))
    );
    if (!sorted.length) return;
    setErr('');
    setMsg('');
    setShiftEditOriginalIds(sorted.map((r) => r.id));
    setShiftForm({
      factory_id: String(site.id),
      name: shiftName,
      grace_minutes: String(sorted[0]?.grace_minutes ?? 10),
      slots: sorted.map((r) => ({
        shift_id: r.id,
        weekday: String(r.weekday),
        start_time: r.start_time,
        end_time: r.end_time,
      })),
    });
    setShowShiftModal(true);
  };

  const loadData = async () => {
    if (!selectedCompanyId) {
      setSites([]);
      setShiftsBySite({});
      return;
    }
    const fRes = await managementAPI.factories(Number(selectedCompanyId));
    const sitesData = fRes.data || [];
    setSites(sitesData);
    const shiftMap = {};
    await Promise.all(
      sitesData.map(async (site) => {
        const shRes = await managementAPI.shifts(site.id);
        shiftMap[String(site.id)] = shRes.data || [];
      })
    );
    setShiftsBySite(shiftMap);
  };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await loadData();
      } catch (e) {
        setErr(e?.response?.data?.detail || 'No se pudo cargar configuración');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyId]);

  const saveSite = async () => {
    const companyId = selectedCompanyId;
    if (!companyId) return setErr('Selecciona una empresa en el header');
    if (!siteForm.name?.trim()) {
      return setErr('El nombre de la sede es obligatorio');
    }
    if (siteForm.lat === '' || siteForm.lng === '') {
      return setErr('Falta la ubicación: elige una dirección de la lista o rellena latitud y longitud');
    }
    try {
      setErr('');
      setMsg('');
      const payload = {
        company_id: companyId,
        name: siteForm.name.trim(),
        address: siteForm.address || 'Dirección pendiente',
        latitude: Number(siteForm.lat || 0),
        longitude: Number(siteForm.lng || 0),
        radius_meters: Number(siteForm.radius || 100),
      };
      if (editingSiteId) {
        await managementAPI.updateFactory(editingSiteId, payload);
      } else {
        await managementAPI.createFactory(payload);
      }
      setSiteForm({
        name: '',
        address: '',
        lat: '',
        lng: '',
        radius: '100',
      });
      setEditingSiteId(null);
      setShowSiteModal(false);
      await loadData();
      if (onCompanyCreated) await onCompanyCreated();
      setMsg(editingSiteId ? 'Sede actualizada correctamente' : 'Sede creada correctamente');
    } catch (e) {
      setErr(e?.response?.data?.detail || (editingSiteId ? 'No se pudo actualizar sede' : 'No se pudo crear sede'));
    }
  };

  const saveShift = async () => {
    if (!selectedCompanyId) return setErr('Selecciona una empresa en el header');
    if (!shiftForm.factory_id) return setErr('Selecciona una sede para el turno');
    if (!shiftForm.name.trim()) return setErr('El nombre del turno es obligatorio');
    if (!shiftForm.slots.length) return setErr('Agrega al menos un día/horario al turno');
    const invalidSlot = shiftForm.slots.find((s) => !s.start_time || !s.end_time || s.start_time === s.end_time);
    if (invalidSlot) return setErr('Revisa horarios del turno: inicio y fin no pueden ser iguales');
    const isEdit = Array.isArray(shiftEditOriginalIds) && shiftEditOriginalIds.length > 0;
    try {
      setErr('');
      setMsg('');
      if (isEdit) {
        const kept = new Set(shiftForm.slots.map((s) => s.shift_id).filter(Boolean));
        await Promise.all(
          shiftEditOriginalIds
            .filter((id) => !kept.has(id))
            .map((id) => managementAPI.updateShift(id, { active: false }))
        );
        const payloadBase = {
          name: shiftForm.name.trim(),
          grace_minutes: Number(shiftForm.grace_minutes || 10),
        };
        await Promise.all(
          shiftForm.slots.map((slot) => {
            const body = {
              ...payloadBase,
              weekday: Number(slot.weekday),
              start_time: slot.start_time,
              end_time: slot.end_time,
            };
            if (slot.shift_id) {
              return managementAPI.updateShift(slot.shift_id, body);
            }
            return managementAPI.createShift({
              factory_id: Number(shiftForm.factory_id),
              ...body,
              active: true,
            });
          })
        );
        closeShiftModal();
        await loadData();
        setMsg('Turno actualizado correctamente');
      } else {
        await Promise.all(
          shiftForm.slots.map((slot) =>
            managementAPI.createShift({
              factory_id: Number(shiftForm.factory_id),
              name: shiftForm.name.trim(),
              weekday: Number(slot.weekday),
              start_time: slot.start_time,
              end_time: slot.end_time,
              grace_minutes: Number(shiftForm.grace_minutes || 10),
              active: true,
            })
          )
        );
        setShiftForm((f) => ({
          ...f,
          name: '',
          grace_minutes: '10',
          slots: [
            { weekday: '1', start_time: '13:00', end_time: '19:00' },
            { weekday: '6', start_time: '13:00', end_time: '18:00' },
          ],
        }));
        closeShiftModal();
        await loadData();
        setMsg('Turno creado correctamente');
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || (isEdit ? 'No se pudo guardar el turno' : 'No se pudo crear turno'));
    }
  };

  const updateShiftSlot = (idx, patch) => {
    setShiftForm((prev) => ({
      ...prev,
      slots: prev.slots.map((slot, i) => (i === idx ? { ...slot, ...patch } : slot)),
    }));
  };

  const addShiftSlot = () => {
    setShiftForm((prev) => ({
      ...prev,
      slots: [...prev.slots, { weekday: '1', start_time: '08:00', end_time: '17:00' }],
    }));
  };

  const removeShiftSlot = (idx) => {
    setShiftForm((prev) => ({
      ...prev,
      slots: prev.slots.filter((_, i) => i !== idx),
    }));
  };

  return (
    <div className="space-y-4">
      <div className="card-mobile">
        <h2 className="text-lg font-semibold text-gray-900">Configuración</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Ajustes de la empresa elegida en el encabezado. Elige un área:
        </p>
        <div
          className="flex mt-3 rounded-xl bg-slate-100/90 p-1 gap-1"
          role="tablist"
          aria-label="Secciones de configuración"
        >
          {[
            { id: 'sedes', label: 'Sedes' },
            { id: 'turnos', label: 'Turnos' },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={configSection === tab.id}
              onClick={() => setConfigSection(tab.id)}
              className={`flex-1 min-w-0 rounded-lg py-2 px-2 text-sm font-medium transition-colors ${
                configSection === tab.id
                  ? 'bg-white text-gray-900 shadow-sm ring-1 ring-slate-200/80'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {configSection === 'sedes' && (
        <div className="card-mobile">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <h3 className="font-semibold text-gray-900">Sedes</h3>
              <p className="text-sm text-gray-500">Altas y listado de sedes o plantas.</p>
            </div>
            <button
              className="btn-mobile btn-primary text-sm py-1.5 px-3 shrink-0"
              onClick={openNewSiteModal}
              disabled={!selectedCompanyId || loading}
            >
              + Nueva Sede
            </button>
          </div>
          {sites.length === 0 ? (
            <p className="text-sm text-gray-500">
              {!selectedCompanyId ? 'Selecciona una empresa en el header.' : 'Aún no hay sedes para esta empresa.'}
            </p>
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {sites.map((s) => (
                <article key={s.id} className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                  <div className="p-3 border-b border-slate-100">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h4 className="font-semibold text-gray-900">{s.name}</h4>
                        <p className="text-sm text-gray-600 mt-0.5">{s.address}</p>
                      </div>
                      <button
                        type="button"
                        className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white p-2 text-slate-700 hover:bg-slate-50 shrink-0 touch-feedback"
                        onClick={() => openEditSiteModal(s)}
                        title="Editar sede"
                        aria-label="Editar sede"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div className="h-44 bg-slate-100">
                    <iframe
                      title={`Mapa de ${s.name}`}
                      className="w-full h-full border-0"
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      src={`https://www.google.com/maps?q=${s.latitude},${s.longitude}&z=16&output=embed`}
                    />
                  </div>
                  <div className="p-3 text-xs">
                    <div className="rounded-lg bg-slate-50 p-2">
                      <p className="text-slate-500">Rango (metros)</p>
                      <p className="font-medium text-slate-800">{s.radius_meters} m</p>
                    </div>
                  </div>
                  <div className="px-3 pb-3">
                    <p className="text-xs text-slate-500 mb-1">Turnos asignados</p>
                    {(() => {
                      const rows = (shiftsBySite[String(s.id)] || []).filter((r) => r.active !== false);
                      if (!rows.length) return <p className="text-xs text-gray-500">Sin turnos.</p>;
                      const grouped = rows.reduce((acc, r) => {
                        if (!acc[r.name]) acc[r.name] = [];
                        acc[r.name].push(r);
                        return acc;
                      }, {});
                      return (
                        <div className="space-y-1">
                          {Object.entries(grouped).map(([name, detailRows]) => {
                            const key = `${s.id}:${name}`;
                            const open = !!expandedShiftGroups[key];
                            const sorted = [...detailRows].sort((a, b) => a.weekday - b.weekday || a.start_time.localeCompare(b.start_time));
                            return (
                              <div key={key} className="rounded-lg border border-slate-200">
                                <button
                                  type="button"
                                  className="w-full text-left px-2 py-1.5 text-xs font-medium text-slate-700 flex items-center justify-between"
                                  onClick={() => setExpandedShiftGroups((prev) => ({ ...prev, [key]: !open }))}
                                >
                                  <span>{name}</span>
                                  <span className="text-slate-500">{open ? 'Ocultar' : 'Detalle'}</span>
                                </button>
                                {open && (
                                  <div className="px-2 pb-2 space-y-0.5">
                                    {sorted.map((r) => (
                                      <p key={r.id} className="text-[11px] text-slate-600">
                                        {weekdayOptions.find((w) => w.value === r.weekday)?.label || r.weekday} · {r.start_time}-{r.end_time}
                                      </p>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      )}

      {configSection === 'turnos' && (
        <div className="card-mobile">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="font-semibold text-gray-900">Turnos</h3>
              <p className="text-sm text-gray-500 mt-1">Crea turnos por sede y consulta los existentes.</p>
            </div>
            <button
              className="btn-mobile btn-primary text-sm py-1.5 px-3 shrink-0"
              onClick={openNewShiftModal}
              disabled={loading || !selectedCompanyId || sites.length === 0}
            >
              + Nuevo Turno
            </button>
          </div>
          <div className="mt-4 space-y-3">
            {sites.length === 0 && <p className="text-sm text-gray-500">Primero crea una sede para poder definir turnos.</p>}
            {sites.map((site) => {
              const rows = (shiftsBySite[String(site.id)] || []).filter((r) => r.active !== false);
              const grouped = rows.reduce((acc, r) => {
                if (!acc[r.name]) acc[r.name] = [];
                acc[r.name].push(r);
                return acc;
              }, {});
              const groupedEntries = Object.entries(grouped);
              return (
                <div key={site.id} className="rounded-xl border border-slate-200 p-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-gray-900">{site.name}</h4>
                    <span className="text-xs text-gray-500">{groupedEntries.length} turnos</span>
                  </div>
                  {rows.length === 0 ? (
                    <p className="text-xs text-gray-500 mt-1">Sin turnos en esta sede.</p>
                  ) : (
                    <div className="mt-2 space-y-1">
                      {groupedEntries.map(([shiftName, details]) => {
                        const sorted = [...details].sort((a, b) => a.weekday - b.weekday || a.start_time.localeCompare(b.start_time));
                        const grace = sorted[0]?.grace_minutes ?? 0;
                        const byTime = sorted.reduce((acc, r) => {
                          const key = `${r.start_time}-${r.end_time}`;
                          if (!acc[key]) acc[key] = { start_time: r.start_time, end_time: r.end_time, weekdays: [] };
                          acc[key].weekdays.push(r.weekday);
                          return acc;
                        }, {});
                        const mergedRows = Object.values(byTime).sort((a, b) => {
                          const da = Math.min(...a.weekdays);
                          const db = Math.min(...b.weekdays);
                          return da - db;
                        });
                        return (
                          <div key={`${site.id}-${shiftName}`} className="rounded-lg bg-slate-50 px-2 py-2 text-xs">
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-medium text-slate-700 min-w-0 truncate">{shiftName}</span>
                              <div className="flex items-center gap-1.5 shrink-0">
                                <span className="text-slate-500">tol {grace}m</span>
                                <button
                                  type="button"
                                  className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white p-1.5 text-slate-700 hover:bg-slate-100 touch-feedback"
                                  onClick={() => openEditShiftModal(site, shiftName, details)}
                                  title="Editar turno"
                                  aria-label="Editar turno"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                            <div className="mt-1 space-y-0.5">
                              {mergedRows.map((r) => (
                                <p key={`${shiftName}-${r.start_time}-${r.end_time}`} className="text-slate-600">
                                  {formatDayGroup(r.weekdays)} · {r.start_time}-{r.end_time}
                                </p>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showSiteModal && (
        <div className="modal-overlay" onClick={() => setShowSiteModal(false)}>
          <div className="modal-mobile center" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="text-lg font-semibold">{editingSiteId ? 'Editar sede' : 'Nueva sede'}</h3>
              <button onClick={() => setShowSiteModal(false)} className="text-2xl">×</button>
            </div>
            <div className="modal-content space-y-2">
              <input
                className="input-mobile"
                placeholder="Nombre sede *"
                value={siteForm.name}
                onChange={(e) => setSiteForm((f) => ({ ...f, name: e.target.value }))}
              />
              <AddressAutocompleteMap
                key={addressMapKey}
                onPlaceSelected={({ address, lat, lng }) => {
                  setSiteForm((f) => ({
                    ...f,
                    address,
                    lat: String(lat),
                    lng: String(lng),
                  }));
                  setMsg(editingSiteId ? 'Dirección y coordenadas actualizadas' : 'Dirección y coordenadas actualizadas desde el mapa');
                  setErr('');
                }}
              />
              <div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span className="text-sm font-semibold text-slate-800 shrink-0">Lat</span>
                  <input
                    className="input-mobile flex-1 min-w-0"
                    inputMode="decimal"
                    value={siteForm.lat}
                    onChange={(e) => setSiteForm((f) => ({ ...f, lat: e.target.value }))}
                  />
                </div>
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span className="text-sm font-semibold text-slate-800 shrink-0">Long</span>
                  <input
                    className="input-mobile flex-1 min-w-0"
                    inputMode="decimal"
                    value={siteForm.lng}
                    onChange={(e) => setSiteForm((f) => ({ ...f, lng: e.target.value }))}
                  />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold text-slate-800 shrink-0">Rango (metros)</span>
                <input
                  className="input-mobile flex-1 min-w-0"
                  inputMode="numeric"
                  value={siteForm.radius}
                  onChange={(e) => setSiteForm((f) => ({ ...f, radius: e.target.value }))}
                />
              </div>
              <button
                className="btn-mobile btn-primary w-full"
                onClick={saveSite}
                disabled={loading || !selectedCompanyId || !siteForm.name?.trim()}
              >
                {editingSiteId ? 'Guardar cambios' : 'Crear sede'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showShiftModal && (
        <div className="modal-overlay" onClick={closeShiftModal}>
          <div className="modal-mobile center" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="text-base font-semibold text-slate-900">
                {shiftEditOriginalIds?.length > 0 ? 'Editar turno' : 'Nuevo turno'}
              </h3>
              <button type="button" onClick={closeShiftModal} className="text-xl leading-none text-slate-600">×</button>
            </div>
            <div className="modal-content space-y-2 text-sm text-slate-800">
              <select
                className="input-mobile text-sm min-h-[2.5rem] py-1"
                value={shiftForm.factory_id}
                onChange={(e) => setShiftForm((f) => ({ ...f, factory_id: e.target.value }))}
                disabled={shiftEditOriginalIds?.length > 0}
              >
                <option value="">Sede...</option>
                {sites.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
              <input
                className="input-mobile text-sm min-h-[2.5rem] py-1"
                placeholder="Nombre turno *"
                value={shiftForm.name}
                onChange={(e) => setShiftForm((f) => ({ ...f, name: e.target.value }))}
              />
              <div className="rounded-xl border border-slate-200 p-2 space-y-2 text-xs">
                <p className="text-[11px] text-slate-500 leading-snug">Días y horarios del turno</p>
                {shiftForm.slots.map((slot, idx) => (
                  <div key={slot.shift_id ?? `new-${idx}`} className="grid grid-cols-12 gap-1.5 items-center">
                    <select
                      className="input-mobile col-span-4 text-xs min-h-[2.25rem] py-0.5 px-1"
                      value={slot.weekday}
                      onChange={(e) => updateShiftSlot(idx, { weekday: e.target.value })}
                    >
                      {weekdayOptions.map((w) => (
                        <option key={w.value} value={w.value}>
                          {w.label}
                        </option>
                      ))}
                    </select>
                    <input
                      className="input-mobile col-span-3 text-xs min-h-[2.25rem] py-0.5 px-1"
                      type="time"
                      value={slot.start_time}
                      onChange={(e) => updateShiftSlot(idx, { start_time: e.target.value })}
                    />
                    <input
                      className="input-mobile col-span-3 text-xs min-h-[2.25rem] py-0.5 px-1"
                      type="time"
                      value={slot.end_time}
                      onChange={(e) => updateShiftSlot(idx, { end_time: e.target.value })}
                    />
                    <button
                      type="button"
                      className="btn-mobile btn-secondary text-[11px] py-0.5 px-1.5 min-h-0 col-span-2 leading-tight"
                      onClick={() => removeShiftSlot(idx)}
                      disabled={shiftForm.slots.length <= 1}
                    >
                      Quitar
                    </button>
                  </div>
                ))}
                <button type="button" className="btn-mobile btn-secondary text-[11px] py-1 px-2 min-h-0 w-full" onClick={addShiftSlot}>
                  + Agregar día/horario
                </button>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold text-slate-800 shrink-0">Tolerancia (minutos)</span>
                <input
                  className="input-mobile flex-1 min-w-0 text-sm min-h-[2.5rem] py-1"
                  type="number"
                  min="0"
                  max="180"
                  inputMode="numeric"
                  value={shiftForm.grace_minutes}
                  onChange={(e) => setShiftForm((f) => ({ ...f, grace_minutes: e.target.value }))}
                />
              </div>
              <button
                className="btn-mobile btn-primary w-full text-sm py-2 min-h-0"
                onClick={saveShift}
                disabled={loading || !selectedCompanyId || !shiftForm.factory_id || !shiftForm.name.trim()}
              >
                {shiftEditOriginalIds?.length > 0 ? 'Guardar cambios' : 'Crear turno'}
              </button>
            </div>
          </div>
        </div>
      )}

      <AppToast
        message={err || msg}
        variant={err ? 'error' : 'success'}
        onClose={() => {
          setErr('');
          setMsg('');
        }}
      />
    </div>
  );
};

export default OwnerConfiguration;
